import React, { useState } from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Typography,
  IconButton,
  Grid,
  Box,
  Button,
  Tooltip,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import CalendarTodayOutlinedIcon from "@mui/icons-material/CalendarTodayOutlined";
import AccessTimeOutlinedIcon from "@mui/icons-material/AccessTimeOutlined";
import ContentCopyIcon from "@mui/icons-material/ContentCopy";
import moment from "moment";

const WebinarPreviewModal = ({
  open,
  onClose,
  webinarData,
  courseNames,
  onConfirm,
  loading = false,
  mode = "create",
}) => {
  const [copied, setCopied] = useState(false);

  if (!webinarData) return null;

  const isEdit = mode === "edit";

  const handleCopyUrl = (url) => {
    if (!url) return;
    navigator.clipboard.writeText(url);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  return (
    <Dialog
      open={open}
      onClose={onClose}
      fullWidth
      maxWidth="md"
      PaperProps={{
        sx: {
          borderRadius: "16px",
          p: 1,
          maxWidth: 740,
        },
      }}
    >
      <DialogTitle
        display="flex"
        justifyContent="space-between"
        alignItems="center"
        sx={{ pb: 3, pt: 1.5, px: 2 }}
      >
        <Typography variant="h4" fontWeight={700} color="#1E293B" fontSize="1.9rem">
          {isEdit ? "Review Updated Webinar Details" : "Review Webinar Details"}
        </Typography>
        <IconButton onClick={onClose} size="small" sx={{ color: "#e7123d" }}>
          <CloseIcon fontSize="medium" />
        </IconButton>
      </DialogTitle>

      <DialogContent sx={{ px: 2, py: 1 }}>
        <Box display="flex" flexDirection="column" gap={2}>
          <Grid container spacing={1.5} alignItems="center">
            {/* Webinar Name */}
            <Grid item xs={4}>
              <Typography variant="body2" color="#64748B" fontWeight={500}>
                Webinar Title
              </Typography>
            </Grid>
            <Grid item xs={8}>
              <Typography variant="body2" color="#0F172A" fontWeight={700}>
                {webinarData.name || "N/A"}
              </Typography>
            </Grid>

            {/* Courses */}
            <Grid item xs={4}>
              <Typography variant="body2" color="#64748B" fontWeight={500}>
                Courses
              </Typography>
            </Grid>
            <Grid item xs={8}>
              <Typography variant="body2" color="#334155" fontWeight={500}>
                {courseNames ||
                  (Array.isArray(webinarData.selectedCourseNames)
                    ? webinarData.selectedCourseNames.join(", ")
                    : "N/A")}
              </Typography>
            </Grid>

            {/* Type */}
            <Grid item xs={4}>
              <Typography variant="body2" color="#64748B" fontWeight={500}>
                Class Type
              </Typography>
            </Grid>
            <Grid item xs={8}>
              <Typography variant="body2" color="#334155" fontWeight={500}>
                {webinarData.is_recurring ? "Recurring" : "One-Time"}
              </Typography>
            </Grid>

            {/* Date & Time */}
            <Grid item xs={4} sx={{ alignSelf: "flex-start", pt: 1 }}>
              <Typography variant="body2" color="#64748B" fontWeight={500}>
                Date & Time
              </Typography>
            </Grid>
            <Grid item xs={8}>
              <Box display="flex" alignItems="center" gap={1} mb={0.5}>
                <CalendarTodayOutlinedIcon sx={{ fontSize: 16, color: "#64748B" }} />
                <Typography variant="body2" color="#334155" fontWeight={600}>
                  {webinarData.date
                    ? moment(webinarData.date).format("DD MMMM YYYY")
                    : "N/A"}
                </Typography>
              </Box>
              <Box display="flex" alignItems="center" gap={1}>
                <AccessTimeOutlinedIcon sx={{ fontSize: 16, color: "#64748B" }} />
                <Typography variant="body2" color="#334155" fontWeight={500}>
                  {webinarData.time} {webinarData.time_ext} ({webinarData.duration} Minutes)
                </Typography>
              </Box>
            </Grid>

            {/* Timezone */}
            <Grid item xs={4}>
              <Typography variant="body2" color="#64748B" fontWeight={500}>
                Time Zone
              </Typography>
            </Grid>
            <Grid item xs={8}>
              <Typography variant="body2" color="#334155" fontWeight={500}>
                {webinarData.timezone || "Asia/Kolkata"}
              </Typography>
            </Grid>

            {/* Notes */}
            <Grid item xs={4}>
              <Typography variant="body2" color="#64748B" fontWeight={500}>
                Webinar Note
              </Typography>
            </Grid>
            <Grid item xs={8}>
              <Typography variant="body2" color="#334155" fontWeight={500}>
                {webinarData.note || "N/A"}
              </Typography>
            </Grid>

            {/* Meeting URL */}
            <Grid item xs={4} sx={{ alignSelf: "flex-start" }}>
              <Typography variant="body2" color="#64748B" fontWeight={500}>
                Meeting URL
              </Typography>
            </Grid>
            <Grid item xs={8}>
              <Box display="flex" alignItems="center" gap={0.5}>
                <Typography
                  variant="body2"
                  component="a"
                  href={webinarData.join_url}
                  target="_blank"
                  rel="noopener noreferrer"
                  sx={{
                    color: "#7C2DF4",
                    wordBreak: "break-all",
                    textDecoration: "none",
                    fontSize: "0.85rem",
                    "&:hover": { textDecoration: "underline" },
                  }}
                >
                  {webinarData.join_url || "N/A"}
                </Typography>
                {webinarData.join_url && (
                  <Tooltip title={copied ? "Copied!" : "Copy URL"}>
                    <IconButton
                      size="small"
                      onClick={() => handleCopyUrl(webinarData.join_url)}
                      sx={{ color: "#94A3B8", p: 0.5 }}
                    >
                      <ContentCopyIcon sx={{ fontSize: 16 }} />
                    </IconButton>
                  </Tooltip>
                )}
              </Box>
            </Grid>
          </Grid>
        </Box>
      </DialogContent>

      <DialogActions sx={{ px: 2, pb: 2, pt: 1, justifyContent: "flex-end", gap: 1 }}>
        <Button
          variant="outlined"
          onClick={onClose}
          disabled={loading}
          sx={{
            borderColor: "#CBD5E1",
            color: "#334155",
            textTransform: "none",
            fontWeight: 600,
            borderRadius: "8px",
            px: 2.5,
            py: 0.5,
          }}
        >
          Back to Edit
        </Button>

        <Button
          variant="contained"
          onClick={onConfirm}
          disabled={loading}
          sx={{
            bgcolor: "#6E3FF3",
            color: "#FFFFFF",
            textTransform: "none",
            fontWeight: 600,
            borderRadius: "8px",
            px: 3,
            py: 0.75,
            boxShadow: "none",
            "&:hover": {
              bgcolor: "#5B2ECC",
              boxShadow: "none",
            },
          }}
        >
          {loading
            ? isEdit
              ? "Updating..."
              : "Creating..."
            : isEdit
            ? "Confirm & Update"
            : "Create Webinar"}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default WebinarPreviewModal;